import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import dayjs from "dayjs";
import { RefreshCw, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const statusMeta = {
  TRIAL: { label: "Trial", className: "bg-amber-100 text-amber-800 border-0" },
  ACTIVE: { label: "Aktif", className: "bg-emerald-100 text-emerald-800 border-0" },
  EXPIRED: { label: "Expired", className: "bg-red-100 text-red-800 border-0" },
  LEGACY: { label: "Legacy", className: "bg-zinc-200 text-zinc-700 border-0" },
};

function formatDate(value) {
  return value ? dayjs(value).format("DD MMM YYYY, HH:mm") : "—";
}

function StatusBadge({ status }) {
  const meta = statusMeta[status] || statusMeta.LEGACY;
  return <Badge className={meta.className}>{meta.label}</Badge>;
}

export default function Subscriptions() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], counts: { trial: 0, active: 0, expired: 0, legacy: 0 } });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");
  const [pinDialog, setPinDialog] = useState(null);
  const [pin, setPin] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get("/subscription/summary");
      setData(result);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat data langganan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startActivate = (item, plan) => {
    setPin("");
    setPinDialog({ item, plan });
  };

  const activate = async () => {
    if (!pinDialog) return;
    if (!/^\d{6}$/.test(pin)) {
      toast.error("PIN harus 6 digit angka");
      return;
    }

    const { item, plan } = pinDialog;
    const key = `${item.id}:${plan}`;
    setActing(key);
    try {
      await api.post(`/subscriptions/${item.id}/activate`, { plan, pin });
      toast.success(`${item.name} aktif ${plan} selama 30 hari`);
      setPinDialog(null);
      setPin("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengaktifkan langganan");
    } finally {
      setActing("");
    }
  };

  const cards = [
    ["Trial", data.counts.trial],
    ["Aktif", data.counts.active],
    ["Expired", data.counts.expired],
    ["Legacy", data.counts.legacy],
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Langganan</h1>
          <p className="text-sm text-zinc-500 mt-1">Pantau trial 14 hari dan langganan bulanan semua outlet.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "mr-2 animate-spin" : "mr-2"} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(([label, value]) => (
          <div key={label} className="bg-white border border-zinc-200 rounded-2xl p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">{label}</div>
            <div className="text-2xl font-bold mt-1">{value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Outlet</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Plan</th>
                <th className="text-left px-5 py-3 font-semibold">Sisa</th>
                <th className="text-left px-5 py-3 font-semibold">Berakhir</th>
                <th className="text-left px-5 py-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item.id} className="border-t border-zinc-100">
                  <td className="px-5 py-4">
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-xs text-zinc-400">/{item.slug}</div>
                  </td>
                  <td className="px-5 py-4"><StatusBadge status={item.subscription_status} /></td>
                  <td className="px-5 py-4 font-medium">{item.plan}</td>
                  <td className="px-5 py-4">
                    {item.remaining_days == null ? "—" : `${item.remaining_days} hari`}
                  </td>
                  <td className="px-5 py-4 text-zinc-600">
                    {formatDate(item.subscription_status === "TRIAL" ? item.trial_ends_at : item.expires_at)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={!!acting || user?.has_pin === false}
                        onClick={() => startActivate(item, "BESTIE")}
                      >
                        {acting === `${item.id}:BESTIE` ? "..." : item.subscription_status === "ACTIVE" && item.plan === "BESTIE" ? "BESTIE +30 hari" : "Aktifkan BESTIE"}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={!!acting || user?.has_pin === false}
                        onClick={() => startActivate(item, "CARE")}
                      >
                        {acting === `${item.id}:CARE` ? "..." : item.subscription_status === "ACTIVE" && item.plan === "CARE" ? "CARE +30 hari" : "Aktifkan CARE"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!acting || user?.has_pin === false}
                        onClick={() => startActivate(item, "SULTAN")}
                      >
                        {acting === `${item.id}:SULTAN` ? "..." : item.subscription_status === "ACTIVE" ? "SULTAN +30 hari" : "Aktifkan SULTAN"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-400">Belum ada outlet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={!!pinDialog} onOpenChange={(open) => !open && setPinDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} /> Konfirmasi PIN Keamanan
            </DialogTitle>
            <DialogDescription>
              Masukkan PIN 6 digit untuk paket {pinDialog?.plan || ""}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="subscription-pin">PIN keamanan</Label>
            <Input
              id="subscription-pin"
              type="password"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6 digit angka"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") activate();
              }}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialog(null)} disabled={!!acting}>
              Batal
            </Button>
            <Button
              onClick={activate}
              disabled={!!acting || pin.length !== 6}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {acting ? "Memproses..." : "Konfirmasi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
